import { cloneDeep } from "lodash";
import { toast } from "react-toastify";

import { REQUEST_STATUSES } from "@/constants/index.js";

import * as types from "./types.js";
import { ExperienceMediaItemService } from "../../../services";

export const getExperienceMediaItems = (propertyId, experienceId, query) => {
  return async (dispatch) => {
    dispatch({
      type: types.SET_EXPERIENCE_MEDIA_ITEMS_REQUEST_STATUS,
      payload: { status: REQUEST_STATUSES.LOADING },
    });

    const { resp, status, err } = await ExperienceMediaItemService.getExperienceMediaItems(
      propertyId,
      experienceId,
      query,
    );

    const payload = {
      propertyId,
      experienceId,
      status,
      err,
      experienceMediaItems: resp.data.reduce((a, v) => ({ ...a, [v.mediaItemId]: v }), {}),
    };

    dispatch({
      type: types.GET_EXPERIENCE_MEDIA_ITEMS,
      payload,
    });
  };
};

export const addExperienceMediaItems = (propertyId, experienceId, mediaItems, options = { showToast: true }) => {
  return async (dispatch, getState) => {
    const { showToast } = options;

    if (!mediaItems.length) {
      return;
    }

    try {
      const key = `${propertyId}/${experienceId}`;
      const currentExperienceMediaItems = cloneDeep(getState().experienceMediaItems[key]?.experienceMediaItems) || {};
      let newCurrentExperienceMediaItems;

      if (mediaItems.length < 3) {
        const results = await Promise.all(
          mediaItems.map((item) =>
            ExperienceMediaItemService.addExperienceMediaItem(propertyId, experienceId, item.id),
          ),
        );

        newCurrentExperienceMediaItems = results.reduce((acc, item) => {
          const { mediaItemId } = item.resp.data;

          return {
            ...acc,
            [mediaItemId]: item.resp.data,
          };
        }, currentExperienceMediaItems);
      } else {
        const results = await ExperienceMediaItemService.bulkAddExperienceMediaItem(propertyId, experienceId, {
          mediaItemIds: mediaItems.map((mediaItem) => mediaItem.id),
        });
        newCurrentExperienceMediaItems = results.resp.data.reduce((acc, mediaItem) => {
          const { mediaItemId } = mediaItem;

          return {
            ...acc,
            [mediaItemId]: mediaItem,
          };
        }, currentExperienceMediaItems);
      }

      const payload = {
        propertyId,
        experienceId,
        experienceMediaItems: newCurrentExperienceMediaItems,
      };

      dispatch({
        type: types.ADD_EXPERIENCE_MEDIA_ITEMS,
        payload,
      });

      if (showToast) {
        toast.success(`Added ${mediaItems.length} media item${mediaItems.length > 1 ? "s" : ""}`, {
          containerId: "dark",
          autoClose: 1500,
        });
      }
    } catch {
      if (showToast) {
        toast.error("Failed to add media items.", { containerId: "dark" });
      }
    }
  };
};

export const removeExperienceMediaItems = (propertyId, experienceId, mediaItems) => {
  return async (dispatch, getState) => {
    if (!mediaItems.length) {
      return;
    }

    try {
      const key = `${propertyId}/${experienceId}`;
      const currentExperienceMediaItems = cloneDeep(getState().experienceMediaItems[key]?.experienceMediaItems) || {};
      let newCurrentExperienceMediaItems;

      if (mediaItems.length < 3) {
        const results = await Promise.all(
          mediaItems.map((item) =>
            ExperienceMediaItemService.deleteExperienceMediaItem(propertyId, experienceId, item.id),
          ),
        );

        newCurrentExperienceMediaItems = results.reduce((acc, item) => {
          const { mediaItemId } = item.resp.data;
          delete acc[mediaItemId];

          return acc;
        }, currentExperienceMediaItems);
      } else {
        const results = await ExperienceMediaItemService.bulkDeleteExperienceMediaItem(propertyId, experienceId, {
          mediaItemIds: mediaItems.map((mediaItem) => mediaItem.id),
        });

        newCurrentExperienceMediaItems = results.resp.data.reduce((acc, mediaItemId) => {
          delete acc[mediaItemId];

          return acc;
        }, currentExperienceMediaItems);
      }

      const payload = {
        propertyId,
        experienceId,
        experienceMediaItems: newCurrentExperienceMediaItems,
      };

      dispatch({
        type: types.REMOVE_EXPERIENCE_MEDIA_ITEMS,
        payload,
      });

      toast.success(`Removed ${mediaItems.length} media item${mediaItems.length > 1 ? "s" : ""}`, {
        containerId: "dark",
        autoClose: 1500,
      });
    } catch {
      toast.error(`Failed to remove media items.`, { containerId: "dark" });
    }
  };
};
